import React from "react";
import { Box, Typography } from "@mui/material";

export interface MetricListItem {
  label: string;
  value: number;
}

interface MetricListProps {
  items: MetricListItem[];
  labelHeader?: string;
  valueLabel?: string;
  limit?: number;
}

const formatCompactNumber = (value: number) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

export const MetricList = ({
  items,
  labelHeader = "Item",
  valueLabel,
  limit,
}: MetricListProps) => {
  const displayed = limit ? items.slice(0, limit) : items;
  const maxValue = Math.max(...displayed.map((item) => item.value), 0);

  if (displayed.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data available.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          color: "text.secondary",
        }}
      >
        <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
          {labelHeader}
        </Typography>
        <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
          {valueLabel ?? "Value"}
        </Typography>
      </Box>
      {displayed.map((item) => {
        const widthPercent = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
        return (
          <Box
            key={item.label}
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              pb: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 1,
              }}
            >
              <Typography variant="subtitle1" noWrap>
                {item.label}
              </Typography>
              <Typography variant="subtitle2">
                {formatCompactNumber(item.value)}
              </Typography>
            </Box>
            <Box
              sx={{
                height: 4,
                mt: 0.75,
                backgroundColor: "divider",
                borderRadius: 999,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${widthPercent}%`,
                  backgroundColor: "#2f6fed",
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
